'use client'

import { useState, useContext, useEffect } from 'react'
import { RootContext } from '@/modules/contextService'
import {
  Avatar,
  Dialog,
  DialogTitle,
  Button as MuiButton,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  IconButton,
  Snackbar
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import PersonOffIcon from '@mui/icons-material/PersonOff'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import { PATHWAY } from '@/modules/httpService'
import ThemeClient from '@/app/v1/ThemeClient'
import cookieService from '@/modules/cookieService'
import { useTranslations } from 'next-intl'

interface ISubscribeButtonProps {
  username: string
  userId: string
  isFollowing?: boolean
  disableFollow?: boolean
}

function SubscribeButton ({ username, userId, disableFollow }: ISubscribeButtonProps) {
  const t = useTranslations('PostMenu')
  const { rootData } = useContext(RootContext)
  const { user } = rootData
  const [subscriberDialogOpen, setSubscriberDialogOpen] = useState<boolean>(false)
  const [loginRequiredOpen, setLoginRequiredOpen] = useState<boolean>(false)
  const [updating, setUpdating] = useState<boolean>(true)
  const [isFollowing, setIsFollowing] = useState<boolean>(false)
  const [message, setMessage] = useState('')
  const [selectedTheme, setSelectedTheme] = useState('')

  const isSelf = user?.username === username

  async function handleFollow (): Promise<void> {
    try {
      setUpdating(true)
      await PATHWAY.post('/self/follow', {
        id: userId
      })
      setIsFollowing(true)
      // if (user) {
      //   if (!user.zoom) user.zoom = {}
      //   if (!user.zoom.info) user.zoom.info = { isFollowing: false, followers: 0, following: 0, posts: 0 }
      //   user.zoom.info.isFollowing = true
      //   setUser(user)
      // }
      setUpdating(false)
      setMessage(t('subscribed'))
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err.message)
        console.log(err.stack)
      }
    }
  }

  async function handleUnfollow (): Promise<void> {
    try {
      setUpdating(true)
      await PATHWAY.delete(`/self/follow/${userId}`)
      setIsFollowing(false)
      setUpdating(false)
      setMessage(t('unsubscribed'))
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err.message)
        console.log(err.stack)
      }
    }
  }

  async function loadFollower () {
    setUpdating(true)
    const res = await PATHWAY.get(`/self/follow?username=${username}`)
    if (res.data.data.followers[0]) {
      setIsFollowing(true)
    }
    setUpdating(false)
  }

  useEffect(() => {
    // lookup if user is following the target user
    if (user) loadFollower()
  }, [user])

  useEffect(() => {
    // lookup if user is following the target user
    setUpdating(false)
  }, [!user])

  useEffect(() => {
    const storedTheme = cookieService.getCookie('theme')
    setSelectedTheme(storedTheme || 'dark')
  }, [])

  let buttonSelect = 'follow'

  if (isFollowing) {
    buttonSelect = 'following'
    if (updating) {
      buttonSelect = 'follow'
    }
  } else if (isSelf) {
    buttonSelect = 'following'
  }

  if (!user) buttonSelect = 'follow'

  return (
    <ThemeClient theme={selectedTheme}>
      {buttonSelect === 'follow' &&
        <MuiButton
          variant='contained'
          color='primary'
          style={{ textTransform: 'none' }}
          disabled={disableFollow}
          startIcon={<PersonAddAlt1Icon />}
          onClick={async () => {
            if (user && handleFollow !== undefined) await handleFollow()
            if (!user) setLoginRequiredOpen(true)
          }}
        >
          {t('subscribe')}
        </MuiButton>
      }
      {buttonSelect === 'following' &&
        <MuiButton
          variant='outlined'
          color='primary'
          style={{ textTransform: 'none' }}
          disabled={disableFollow || updating}
          onClick={() => {
            if (user) setSubscriberDialogOpen(true)
          }}
          endIcon={<KeyboardArrowDownIcon />}
        >
          {t('following')}
        </MuiButton>
      }
      <Dialog
        open={loginRequiredOpen}
        maxWidth='xs'
        fullWidth
        onClose={() => setLoginRequiredOpen(false)}
      >
        {/* <DialogTitle style={{ textAlign: 'center', borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>{username}</DialogTitle> */}
        <IconButton
          aria-label="close"
          onClick={() => setLoginRequiredOpen(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme: any) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          {t('subscriber-warning')}
        </DialogContent>
      </Dialog>

      <Dialog
        open={subscriberDialogOpen}
        maxWidth='xs'
        fullWidth
        onClose={() => setSubscriberDialogOpen(false)}
      >
        <DialogTitle style={{ textAlign: 'center', borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>{username}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setSubscriberDialogOpen(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme: any) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <List sx={{ pt: 0 }}>
            <ListItem disableGutters>
              <ListItemButton
                autoFocus
                onClick={() => {
                  //
                }}
              >
                <ListItemAvatar>
                  <Avatar>
                    <PersonOffIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={t('unsubscribe')}
                  onClick={async () => {
                    setSubscriberDialogOpen(false)
                    if (handleUnfollow !== undefined) await handleUnfollow()
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={message !== ''}
        autoHideDuration={6000}
        onClose={() => setMessage('')}
        message={message}
      />
    </ThemeClient>
  )
}

export default SubscribeButton
